import { useState, type ReactNode } from "react";
import { isShelved } from "@lms/shared";

import type { AlarmRow } from "./AlarmBanner.js";
import { Icon } from "./Icon.js";
import { NotificationsPanel } from "./NotificationsPanel.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";

export type View = "home" | "emissions" | "settings";

/**
 * L'intestazione, portata da `Home.qml`.
 *
 * La forma e' quella dell'app desktop, e dice come si naviga: al **centro**
 * c'e' soltanto il selettore GRIGLIA / MAPPA SITO, e a **destra** le icone.
 * Non ci sono schede di testo per allarmi, emissioni e configurazione, perche'
 * quelle non sono pari alla griglia degli impianti: la griglia e' il posto
 * dove si sta, le altre sono cose che si aprono.
 *
 * - la **campanella** apre il pannello delle notifiche, che scende sopra
 *   quello che si stava guardando. Un allarme e' una cosa che interrompe:
 *   metterlo in una pagina separata vuol dire che per vederlo bisogna aver
 *   deciso di andarci, e chi sorveglia sta guardando i video.
 * - il **grafico** apre le emissioni, l'**ingranaggio** la configurazione.
 *   Gli utenti si raggiungono da li', dalla scheda AMMINISTRAZIONE, che e'
 *   l'unica strada anche nell'app desktop.
 *
 * Le icone che aprono una schermata sono gattate dal permesso, come nel
 * `.qml`. La campanella no: gli allarmi li vede chiunque sia collegato, ed e'
 * il senso del programma.
 */
export function Shell({
  view,
  onView,
  live,
  criticalCount,
  alarms,
  clusterNames,
  onAlarmsChanged,
  center,
  bare,
  children,
}: {
  view: View;
  onView: (v: View) => void;
  /** La telemetria sta arrivando? Non e' un dettaglio tecnico: senza, una
   *  pagina ferma su numeri vecchi sembra una pagina che funziona. Nell'app
   *  desktop non serve — il programma gira sulla macchina che legge il
   *  sensore — qui la rete puo' cadere senza che nessuno se ne accorga. */
  live: boolean;
  criticalCount?: number | undefined;
  alarms: AlarmRow[];
  clusterNames: Record<string, string>;
  onAlarmsChanged: () => void;
  /** Il selettore GRIGLIA / MAPPA SITO, che esiste solo sulla griglia. */
  center?: ReactNode;
  /** Schermata a tutta finestra, senza la barra dell'applicazione.
   *
   *  Nell'app desktop l'impianto singolo NON e' una pagina dentro una
   *  cornice: occupa tutta la finestra, e si esce con la freccia in alto a
   *  sinistra. La barra sopra il video toglie 64 px all'immagine e mette a
   *  portata di clic la campanella e l'ingranaggio mentre si sta guidando la
   *  testa — che e' esattamente quando non si vuole cambiare schermata. */
  bare?: boolean;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const { phase, logout, can } = useSession();
  const user = phase.kind === "authenticated" ? phase.user : undefined;
  const [notifOpen, setNotifOpen] = useState(false);

  const unacked = alarms.filter((a) => a.state === "UNACK" || a.state === "RTNUN").length;
  const shelved = alarms.filter((a) => isShelved(a.state)).length;

  const initials = (user?.displayName ?? user?.username ?? "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

  if (bare) {
    return <div className="shell shell--bare">{children}</div>;
  }

  return (
    <div className="shell">
      <header className="topbar">
        <img className="topbar__logo" src="/assets/pergam_logo.png" alt="Pergam Group" />
        <div className="topbar__brand">
          <span className="brand">
            LMS<span>mini</span>
          </span>
          <span className="topbar__subtitle">{t("app.subtitle")}</span>
        </div>

        <span className="emissions__spacer" />
        {center}
        <span className="emissions__spacer" />

        <div className="topbar__right">
          {criticalCount !== undefined && criticalCount > 0 && (
            <span className="pill pill--critical">
              <Icon name="shield-alert" size={14} />
              {criticalCount} {t("home.critical")}
            </span>
          )}

          <span className={`pill ${live ? "pill--ok" : "pill--warn"}`}>
            <i className={live ? "dot dot--live" : "dot"} />
            {live ? t("card.live") : t("card.streamLost")}
          </span>

          <button
            className={`iconbtn${unacked > 0 ? " iconbtn--alert" : ""}`}
            onClick={() => setNotifOpen(!notifOpen)}
            title={t("home.notifications")}
          >
            <Icon name="bell" size={18} />
            {unacked > 0 && <span className="iconbtn__badge">{unacked > 9 ? "9+" : unacked}</span>}
            {/* Solo silenziati: pastiglia ambra. "Nessuno da confermare" non
                vuol dire "niente in sospeso". */}
            {unacked === 0 && shelved > 0 && (
              <span className="iconbtn__badge iconbtn__badge--shlv">
                {shelved > 9 ? "9+" : shelved}
              </span>
            )}
          </button>

          {can("viewEmissions") && (
            <button
              className={`iconbtn${view === "emissions" ? " iconbtn--on" : ""}`}
              onClick={() => onView("emissions")}
              title={t("emissions.title")}
            >
              <Icon name="bar-chart-3" size={18} />
            </button>
          )}
          {can("accessSettings") && (
            <button
              className={`iconbtn${view === "settings" ? " iconbtn--on" : ""}`}
              onClick={() => onView("settings")}
              title={t("settings.title")}
            >
              <Icon name="settings" size={18} />
            </button>
          )}

          <span className="topbar__divider" />

          {user && (
            <div className="topbar__user">
              <div className="topbar__userName">{user.displayName || user.username}</div>
              <div className="topbar__userRole">
                {t("nav.level")} {user.level} — {user.role}
              </div>
            </div>
          )}
          {user && <div className="avatar">{initials}</div>}

          <button className="iconbtn" onClick={() => void logout()} title={t("nav.logout")}>
            <Icon name="log-out" size={18} />
          </button>
        </div>
      </header>

      {notifOpen && (
        <NotificationsPanel
          alarms={alarms}
          clusterNames={clusterNames}
          onClose={() => setNotifOpen(false)}
          onChanged={onAlarmsChanged}
        />
      )}

      <main className="shell__body">{children}</main>
    </div>
  );
}
